import { useEffect } from 'react';
import { fromInput } from 'src/functions/fromInput';

/**
 * Navigation hook used to handle keyboard and touch events to navigate between
 * @param onLeft the function to call when the user want to go to the left
 * @param onRight the function to call when the user want to go to the right
 * @param canLeft is the user allowed to go to the left
 * @param canRight is the user allowed to go to the right
 */
function useNavigation(
	onLeft: () => void,
	onRight: () => void,
	canLeft: boolean = true,
	canRight: boolean = true,
	canOverride = false
) {
	useEffect(() => {
		// start touch event x position
		let startX: number | undefined = 0;
		// end touch event x position
		let startY: number | undefined = 0;

		// add keayboard left arrow or A and right arrow or D to navigate between subpages
		const handleKeyDown = (e: KeyboardEvent) => {
			// to disable keyboard event when we are typing on input
			if (fromInput(e) || (!e.isForced && document.body.classList.contains('no-navigation'))) return;

			if (e.key === 'ArrowLeft' || e.key === 'a') {
				if (!canLeft && !canOverride) return;
				onLeft();
			} else if (e.key === 'ArrowRight' || e.key === 'd') {
				if (!canRight && !canOverride) return;
				onRight();
			}
		};

		// save start touch event x position and y position
		const handleTouchStart = (e: TouchEvent) => {
			const target = e.target as HTMLElement;

			if (!target || target.closest('.swipe-deactivated')) {
				startX = undefined;
				startY = undefined;
				return;
			}

			const touch = e.touches?.[0];

			if (!touch) return;

			startX = touch.clientX;
			startY = touch.clientY;
		};

		// check if touch event x position is bigger than start touch event x position
		const handleTouchEnd = (e: TouchEvent) => {
			if (document.body.classList.contains('no-swipe') || document.body.classList.contains('no-navigation')) {
				return;
			}

			if (typeof startX === 'undefined' || typeof startY === 'undefined') return;

			const firstTouchEvent = e.changedTouches?.[0];

			if (!firstTouchEvent) return;

			const location = {
				x: firstTouchEvent.clientX, //get the location of the end of the touch
				y: firstTouchEvent.clientY
			};

			// diffs from start to end locations
			const diffX = startX - location.x;
			const diffY = startY - location.y;

			// check if the move is mainly horizontal
			if (Math.abs(diffX) > Math.abs(diffY)) {
				if (diffX < -30) {
					if (!canLeft && !canOverride) return;
					onLeft();
				} else if (diffX > 30) {
					if (!canRight && !canOverride) return;
					onRight();
				}
			}
		};

		document.addEventListener('touchstart', handleTouchStart);
		document.addEventListener('touchend', handleTouchEnd);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('touchstart', handleTouchStart);
			document.removeEventListener('touchend', handleTouchEnd);
		};
	}, [canLeft, canOverride, canRight, onLeft, onRight]);
}

export { useNavigation };
